<template>
  <BaseRender
    class="education-field-mapping"
    showDefaultValue
    :loading="loading"
    :fieldTypeInfo="fieldTypeInfo"
    :fieldInfo="fieldInfo"
    :mappingList="mappingList"
    :jobCustomField="jobCustomField"
    @save="handleSave"
    @jobCustomFieldChange="handleJobCustomFieldChange"
  >
    <template #defaultValue="{ refName }">
      <el-select
        v-model="defaultValue"
        class="education-select"
        clearable
        :placeholder="$t(`${i18nPrefix}.placeholder.defaultValue`)"
        :ref="refName"
        @focus="clearError(refName)"
      >
        <el-option
          v-for="(item, index) in fieldTypeOptions"
          :key="index"
          :value="item.id"
          :label="item.i18nName"
        ></el-option>
      </el-select>
    </template>
    <template #selfFieldRender="{ data }">
      <span class="self-field-name">{{ data.i18nName }}</span>
    </template>
    <template #otherFieldRender="{ data, refName }">
      <el-select
        v-model="data.value"
        class="education-select"
        clearable
        :ref="refName"
        @focus="clearError(refName)"
      >
        <el-option
          v-for="(item, index) in fieldTypeOptions"
          :key="index"
          :value="item.id"
          :label="item.i18nName"
        ></el-option>
      </el-select>
    </template>
  </BaseRender>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import channelId from '../channelId'
import mixin from '../mixin'
import { addI18nProperty } from '@/i18n'

export default {
  name: `channel-${channelId.liepin}-fieldType-education`,
  mixins: [mixin],
  data () {
    return {
      mappingList: [],
      fieldTypeOptions: [],
      jobEducationList: [],
      jobCustomField: {
        show: true,
        fieldId: null,
        field: null,
        fieldList: []
      }
    }
  },
  computed: {
    educationField () {
      return this.fieldInfo.education
    },
    defaultValue: {
      get () {
        return this.educationField.default
      },
      set (value) {
        this.educationField.default = value
      }
    }
  },
  created () {
    this.refreshPageData()
  },
  methods: {
    ...mapActions('jobPosting', ['getPostingFieldOptions']),
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    getDataFromApi () {
      return Promise.all([
        this.getPostingFieldOptions({ fieldTypeId: this.fieldTypeInfo.id, params: { } }).then(
          resp => {
            if (resp) {
              _.forEach(resp, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
              this.fieldTypeOptions = resp
            }
          }
        ),
        this.searchJobCustomField({ status: 1, type: 'SELECT', need_options: true }).then(
          resp => {
            if (resp) {
              _.forEach(resp, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
              this.jobCustomField.fieldList = resp
              if (this.jobCustomField.fieldId) {
                this.jobCustomField.field = _.find(this.jobCustomField.fieldList, { id: this.jobCustomField.fieldId })
              }
            }
          }
        )
      ])
    },
    async refreshPageData (noNeedApiData) {
      this.loading = true
      try {
        const educationField = this.educationField
        if (educationField.default) {
          educationField.default = parseInt(educationField.default)
        }
        let isJobCustomField = false
        if (educationField.data_source === 'CUSTOM_FIELD') {
          const fieldId = educationField.data_key_path
          this.jobCustomField.fieldId = parseInt(fieldId)
          isJobCustomField = true
        }
        if (!noNeedApiData) {
          await this.getDataFromApi()
        }
        const dataMapList = educationField.data_map_list
        const dataMapKeyMap = {}
        _.forEach(dataMapList, item => {
          dataMapKeyMap[item.key] = item
        })

        const selfFieldList = isJobCustomField ? this.jobCustomField.field.options : this.jobEducationList
        this.mappingList = _.map(selfFieldList, (item, index) => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          const dataMap = dataMapKeyMap[item.id]
          if (dataMap) {
            dataMap.key = parseInt(dataMap.key)
            dataMap.value = parseInt(dataMap.value)
          }
          return {
            id: index + 1,
            self: item,
            other: dataMap || { key: item.id, value: null }
          }
        })
      } finally {
        this.loading = false
      }
    },
    handleJobCustomFieldChange (fieldId) {
      const selfFieldList = fieldId ? this.jobCustomField.field.options : this.jobExperienceList
      this.mappingList = _.map(selfFieldList, (item, index) => {
        addI18nProperty(item, 'i18nName', 'cn_name', 'name')
        return {
          id: index + 1,
          self: item,
          other: { key: item.id, value: null }
        }
      })
    },
    handleSave () {
      if (!this.defaultValue) {
        this.setError(['defaultValue'])
        return
      }
      const educationField = this.educationField
      const isJobCustomField = Boolean(this.jobCustomField.fieldId)
      const dataMapList = []
      _.forEach(this.mappingList, item => {
        const otherField = item.other
        const selfField = item.self
        if (!otherField.value) {
          return
        }
        dataMapList.push({
          ...otherField,
          key: selfField.id
        })
      })
      educationField.data_map_list = dataMapList
      if (isJobCustomField) {
        educationField.data_source = 'CUSTOM_FIELD'
        educationField.data_key_path = `${this.jobCustomField.fieldId}`
      } else {
        educationField.data_source = 'JOB'
        educationField.data_key_path = 'experience'
      }
      this.$emit('save')
    }
  }
}
</script>

<style lang="scss" scoped>
.education-field-mapping {
  ::v-deep {
    .el-table {
      .el-table__body {
        .self-field {
          .el-select {
            .el-input__inner {
              padding-left: 5px;
            }
          }
        }

        .other-field {
          .cell {
            padding-left: 5px;
            padding-right: 5px;
          }

          .education-select {
            .el-input__inner {
              padding-left: 5px;
            }
          }
        }
      }
    }

    .education-select {
      width: 100%;
    }
  }
}
</style>
